import { createAsyncThunk, createSlice,  PayloadAction } from "@reduxjs/toolkit";
import onahReducer, { OnahPersistConfig } from "../../persistor";
import { LoginRequestBody, LoginResponseData } from "services/auth/types";
import { login } from "services/auth";
import { decodeJwt } from "helper";
import { BaseResponse } from "interface/baseResponse";
import { keyConstants } from "helper/keyConstants";
import { getProfile, updateProfile } from "services/user";
import { GetProfileResponseBody, UpdateProfileRequest } from "services/user/types";
import { Merchant, MerchantContact } from "interface/merchant";
import { OnahToasts } from "utils/onahToasts";
import { VolumeData } from "services/dashboard/types";
import { getOverview } from "services/dashboard";


export interface UserLoginPayload {
    data: LoginRequestBody;
    onSuccess?: () => void;
    onError?: () => void;
}

type UserState = {
    loading: boolean;
    error: boolean;
    merchant?: Merchant;
    accounts?: VolumeData;
}

const initialState: UserState = {
    loading: false,
    error: false,
    merchant: undefined,
}

export const userLogin = createAsyncThunk<BaseResponse<LoginResponseData> | undefined,  UserLoginPayload>('user/userLogin', async ({data, onError, onSuccess}) => {
    const response = await login(data);
    if(response) {
        const tokenObj = decodeJwt<{
            authID: string;
            iat: number;
            exp: number;
        }>(response.data!.token)
        if(tokenObj) {
            localStorage.setItem(keyConstants.AUTH_TOKEN, response.data!.token);
            localStorage.setItem(keyConstants.EXPIRY_TOKEN_TIME, (tokenObj.exp * 1000).toString());
            onSuccess && onSuccess();
            return response;
        } 
    }
    onError && onError();
    
});

export const getUserProfile = createAsyncThunk<BaseResponse<GetProfileResponseBody> | undefined>('user/getProfile', async () => {
    const response = await getProfile();
    if(response) {
        return response;
    }
});

export const updateUserProfile = createAsyncThunk<BaseResponse<GetProfileResponseBody> | undefined, UpdateProfileRequest>('user/updateProfile', async (data, apiThunk) => {
    const response = await updateProfile(data);
    if(response) {
        apiThunk.dispatch(getUserProfile());
        OnahToasts.success('You have successfully updated your profile', {title: 'Profile Updated'})
        return response;
    }
});

export const getDashboardOverview = createAsyncThunk('user/dash', async (_, thunkApi) => {
    try {
   
      const res = await getOverview({});
      if (res?.data) {
        thunkApi.dispatch(updateAccounts(res.data.balance))
      }
    } catch (error) {

    }
});

export const userSlice = createSlice({
    name:"u",
    initialState: initialState,
    reducers:{
        setLoading:(state, action: PayloadAction<boolean>) => {
            state.loading = action.payload
        },
        setProfile: (state, action: PayloadAction<Merchant>) => {
            state.merchant = action.payload;
        },
        updateContact: (state, action: PayloadAction<MerchantContact>) => {
            state.merchant!.contact = action.payload;
        },
        updateBusinessPreference: (state, action: PayloadAction<Merchant>) => {
            state.merchant!.paymentChannels = action.payload.paymentChannels;
            state.merchant!.receiptRecipients = action.payload.receiptRecipients;
            state.merchant!.logo = action.payload.logo;
        },
        updateApiKey: (state, action: PayloadAction<{apikey: string; publicKey: string}>) => {
            state.merchant!.apiKey.key = action.payload.apikey;
            state.merchant!.apiKey.publicKey = action.payload.publicKey;
        },
        updateSecretKey: (state, action: PayloadAction<{apikey: string;}>) => {
            state.merchant!.apiKey.key = action.payload.apikey;
        },
        updateAccounts: (state, action: PayloadAction<VolumeData>) => {
            state.accounts = action.payload;
        }
    },
    extraReducers:(builders)=> {
        builders.addCase(userLogin.fulfilled, (state, action) => {
            if(action.payload) {
                state.merchant = action.payload.data?.merchant;
            }
        });
        builders.addCase(getUserProfile.fulfilled, (state, action) => {
            if(action.payload) {
                state.merchant = action.payload.data;
            }
        });
    }
})
export const {setLoading, setProfile, updateContact, updateBusinessPreference, updateApiKey, updateSecretKey, updateAccounts} = userSlice.actions;

const authPersistConfig: OnahPersistConfig = {
    key: userSlice.name,
    blacklist: ['error', 'loading']
}

export const userReducer = onahReducer(authPersistConfig,  userSlice.reducer, true);

export const userReducerPath = userSlice.name;